import { api } from './api';
import type { TaskSummary } from './tasks';

/**
 * The current sitting: what you picked to work on now.
 *
 * A focus session deliberately says something the task's own status cannot.
 * `IN_PROGRESS` describes where a task sits and may stay true for a week; this
 * describes what you are doing this hour, and is meant to be wrong by tomorrow.
 *
 * Types are hand-synced with the server's DTO, exactly as lib/tasks.ts is --
 * there is no shared types package, and adding one for four shapes would be the
 * larger change.
 */

/** A task in a session: the task, plus what the *pick* knows about it. */
export type FocusTask = TaskSummary & {
  addedAt: string;
  /** When it was finished *in this session*, which is not the same question as
   *  the task's own `completedAt` once something is reopened. */
  completedAt: string | null;
};

export type FocusSession = {
  id: string;
  startedAt: string;
  tasks: FocusTask[];
};

export type FocusSummary = {
  planned: number;
  completed: number;
  trackedSeconds: number;
  startedAt: string;
  endedAt: string;
};

/** `null` when nothing is open -- including when the server has just swept a
 *  session left running overnight. */
export const getFocusSession = (signal?: AbortSignal) =>
  api.get<{ session: FocusSession | null }>('/api/focus', signal);

/** Opens a session if none is open: picking the first task *is* the start. */
export const addToFocus = (taskId: string) =>
  api.post<{ session: FocusSession }>('/api/focus/tasks', { taskId });

export const removeFromFocus = (taskId: string) =>
  api.del<{ session: FocusSession }>(`/api/focus/tasks/${taskId}`);

/** `summary` is null when there was nothing open to end. */
export const endFocusSession = () =>
  api.post<{ summary: FocusSummary | null }>('/api/focus/end');
